import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import StatStrip from "../../../Component/StatStrip";
import Pill from "../../../Component/Pill";
import InvoiceDrawer from "../../Invoices/components/InvoiceDrawer";
import TablePager from "./TablePager";
import { getRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import { ApiInvoice, inr2, invoiceNo, invoiceStatus, invoiceTotals, planTypeInfo } from "../../Invoices/invoiceUtils";

// Chips come from the invoice_plan_type values the API returns; this is just the display order
const TYPE_ORDER = ["monthly", "quarterly", "yearly", "annually", "wallet_topup", "wallet_deduction"];

interface CustomerInvoicesTabProps {
  userId: string;
  // bump to reload after a plan change / wallet adjustment
  reloadKey?: number;
}

// Customer details → Invoices tab: every cs_invoice of this customer (GET /subscription/invoices?userId=)
const CustomerInvoicesTab = ({ userId, reloadKey }: CustomerInvoicesTabProps) => {
  const navigate = useNavigate();
  const [invoiceList, setInvoiceList] = useState<ApiInvoice[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalInvoices, setTotalInvoices] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stats, setStats] = useState({ invoiced: 0, plans: 0, walletTopups: 0, unpaid: 0 });
  const [typeCounts, setTypeCounts] = useState<Record<string, number>>({});

  // Filters
  const [planType, setPlanType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [openInvoice, setOpenInvoice] = useState<ApiInvoice | null>(null);
  const [autoPrint, setAutoPrint] = useState(false);

  // Fetch Invoice List (all time)
  const getInvoiceList = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getRequest(
        `/subscription/invoices?page=${page}&limit=${pageSize}&userId=${userId}&planType=${encodeURIComponent(planType)}&invoiceStatus=${statusFilter}&search=${encodeURIComponent(searchQuery)}&sort=${sortOrder}`
      );

      if (response.status === 'success') {
        setInvoiceList(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalInvoices(response.pagination.total);
        if (response.typeCounts) setTypeCounts(response.typeCounts);
        if (response.stats) {
          setStats({
            invoiced: response.stats.invoiced.value,
            plans: response.stats.plans.value,
            walletTopups: response.stats.walletTopups.value,
            unpaid: response.stats.unpaid.value,
          });
        }
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading invoices!");
    } finally {
      setIsLoading(false);
    }
  }, [userId, page, pageSize, planType, statusFilter, searchQuery, sortOrder]);

  useEffect(() => {
    getInvoiceList();
  }, [getInvoiceList, reloadKey]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const changeType = (k: string) => { setPlanType(k); setPage(1); };

  const resetFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setSortOrder("newest");
    changeType("all");
  };

  const onPrinted = useCallback(() => setAutoPrint(false), []);

  // All + every invoice_plan_type this customer has (known types first)
  const typeKeys = Object.keys(typeCounts).sort((a, b) => {
    const ia = TYPE_ORDER.indexOf(a), ib = TYPE_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const allCount = Object.values(typeCounts).reduce((s, n) => s + n, 0);
  const filtered = planType !== "all" || statusFilter !== "all" || !!searchQuery;

  return (
    <>
      <StatStrip items={[
        { label: "Total invoiced", value: inr2(stats.invoiced), icon: "ri-file-list-3-line", delta: "paid · all time" },
        { label: "Plans", value: inr2(stats.plans), icon: "ri-price-tag-3-line", delta: "paid · all time" },
        { label: "Wallet top-ups", value: inr2(stats.walletTopups), icon: "ri-wallet-3-line", delta: "paid · all time" },
        { label: "Unpaid", value: stats.unpaid, icon: "ri-error-warning-line", delta: stats.unpaid ? "needs follow-up" : "all settled" },
      ]} />

      <div className="cx-card">
        <div className="cx-card-h">
          <h6>Invoices</h6>
          <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => navigate("/invoices")}><i className="ri-external-link-line"></i>Open in billing</button>
        </div>

        <div className="cx-toolbar">
          <div className="cx-chips">
            <button type="button" className={`cx-chip ${planType === "all" ? "on" : ""}`} onClick={() => changeType("all")}>All <b>{allCount}</b></button>
            {typeKeys.map(k => (
              <button type="button" key={k} className={`cx-chip ${planType === k ? "on" : ""}`} onClick={() => changeType(k)}>
                {planTypeInfo(k)[0]} <b>{typeCounts[k] || 0}</b>
              </button>
            ))}
          </div>
        </div>
        <div className="cx-toolbar">
          <select className="cx-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All status</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
          <select className="cx-select" value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}>
            <option value="newest">Sort: Newest</option>
            <option value="oldest">Sort: Oldest</option>
          </select>
          <div className="sp"></div>
          <input className="cx-input" type="search" placeholder="Invoice no / plan…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
            <i className="ri-refresh-line"></i>Reset
          </button>
        </div>

        <div className="cx-tbl-wrap">
          <table className="cx-tbl">
            <thead>
              <tr><th>Invoice no.</th><th>Date</th><th>Type</th><th>Period</th><th className="r">Amount</th><th>Status</th><th>Payment</th><th className="r">Actions</th></tr>
            </thead>
            <tbody>
              {isLoading && !invoiceList.length ? (
                <tr><td colSpan={8}><div className="cx-empty"><i className="ri-loader-4-line"></i>Loading invoices…</div></td></tr>
              ) : invoiceList.length ? invoiceList.map(i => {
                const t = invoiceTotals(i);
                const [typeLabel, typeIcon] = planTypeInfo(i.invoice_plan_type);
                const [tone, statusLabel] = invoiceStatus(i);
                const isWallet = i.invoice_plan_type === "wallet_topup" || i.invoice_plan_type === "wallet_deduction";
                return (
                  <tr key={i._id} className="click" onClick={() => setOpenInvoice(i)}>
                    <td className="mono fw6">{invoiceNo(i)}</td>
                    <td className="fs12 muted">{i.createdAt ? moment(i.createdAt).format("DD MMM YYYY") : "—"}</td>
                    <td>
                      <span className="cx-ity"><i className={typeIcon}></i>{typeLabel}</span>
                      <div className="fs12 muted">
                        {i.invoice_plan_name}
                        {i.invoice_type && i.invoice_type !== "standard" && <> · {i.invoice_type}</>}
                      </div>
                    </td>
                    <td className="fs12 muted">
                      {!isWallet && i.invoice_plan_start_date && i.invoice_plan_end_date
                        ? `${moment(i.invoice_plan_start_date).format("DD MMM YYYY")} – ${moment(i.invoice_plan_end_date).format("DD MMM YYYY")}`
                        : "—"}
                    </td>
                    <td className="r">
                      <div className="mono fw6">{inr2(t.tot)}</div>
                      <div className="fs12 muted mono">{inr2(t.sub)} + {inr2(t.gst)} GST</div>
                    </td>
                    <td><Pill tone={tone}>{statusLabel}</Pill></td>
                    <td className="fs12" style={{ textTransform: "uppercase" }}>{i.invoice_transaction_id?.method || <span className="muted">—</span>}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="cx-ra">
                        <button type="button" className="v" title="View invoice" onClick={() => setOpenInvoice(i)}><i className="ri-eye-line"></i></button>
                        <button type="button" title="Download PDF" onClick={() => { setAutoPrint(true); setOpenInvoice(i); }}><i className="ri-download-2-line"></i></button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={8}>
                    <div className="cx-empty">
                      <i className="ri-file-search-line"></i>
                      {filtered
                        ? <>No invoices match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button></>
                        : "No invoices for this customer yet."}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePager
          page={page}
          totalPages={totalPages}
          total={totalInvoices}
          shown={invoiceList.length}
          pageSize={pageSize}
          label="invoices"
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </div>

      <InvoiceDrawer
        invoice={openInvoice}
        autoPrint={autoPrint}
        onPrinted={onPrinted}
        onClose={() => { setOpenInvoice(null); setAutoPrint(false); }}
        hideCustomer
      />
    </>
  );
};

export default CustomerInvoicesTab;
